"use client"

import { useState, useEffect } from "react"
import { Form, Button, Alert, Modal, Table } from "react-bootstrap"
import axios from "axios"

const API_BASE = "https://madhusewingmachines.com/api/blogs"
const UPLOADS_BASE = "https://madhusewingmachines.com/uploads"

const emptyForm = {
  title: "",
  author: "",
  category: "",
  content: "",
  tags: "",
}

const AdminBlogs = () => {
  const [formData, setFormData] = useState(emptyForm)
  const [message, setMessage] = useState("")
  const [error, setError] = useState("")
  const [showModal, setShowModal] = useState(false)
  const [blogs, setBlogs] = useState([])
  const [editBlogId, setEditBlogId] = useState(null)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [blogToDelete, setBlogToDelete] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState("")

  useEffect(() => {
    fetchBlogs()
  }, [])

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(API_BASE)
      if (response.data.success) {
        setBlogs(response.data.blogs || [])
      }
    } catch (err) {
      console.error("Error fetching blogs:", err)
      setError("Failed to fetch blogs.")
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setPreviewUrl(URL.createObjectURL(file))
    }
    e.target.value = ""
  }

  const handleRemoveImage = () => {
    setSelectedFile(null)
    setPreviewUrl("")
  }

  const resetForm = () => {
    setFormData(emptyForm)
    setEditBlogId(null)
    setSelectedFile(null)
    setPreviewUrl("")
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")

    try {
      const tagsArray = formData.tags
        ? formData.tags.split(",").map((t) => t.trim()).filter(Boolean)
        : []

      if (editBlogId) {
        // Update
        const payload = new FormData()
        payload.append("title", formData.title)
        payload.append("author", formData.author)
        payload.append("category", formData.category)
        payload.append("content", formData.content)
        payload.append("tags", JSON.stringify(tagsArray))
        if (selectedFile) {
          payload.append("images", selectedFile)
        }

        await axios.put(`${API_BASE}/${editBlogId}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        setMessage("Blog updated successfully!")
      } else {
        // Create
        if (!selectedFile) {
          setError("Please select a cover image for the blog.")
          return
        }
        const payload = new FormData()
        payload.append("title", formData.title)
        payload.append("author", formData.author)
        payload.append("category", formData.category)
        payload.append("content", formData.content)
        payload.append("tags", JSON.stringify(tagsArray))
        payload.append("images", selectedFile)

        await axios.post(API_BASE, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        setMessage("Blog added successfully!")
      }

      fetchBlogs()
      resetForm()
      setShowModal(false)
    } catch (err) {
      console.error("Error details:", err.response?.data || err.message)
      setError(err.response?.data?.message || "Failed to save blog. Please check the data and try again.")
    }
  }

  const handleEdit = (blog) => {
    setFormData({
      title: blog.title || "",
      author: blog.author || "",
      category: blog.category || "",
      content: blog.content || "",
      tags: blog.tags ? blog.tags.join(", ") : "",
    })
    setPreviewUrl(
      blog.images && blog.images.length > 0 ? `${UPLOADS_BASE}/${blog.images[0].url}` : ""
    )
    setSelectedFile(null)
    setEditBlogId(blog._id)
    setShowModal(true)
  }

  const handleDeleteClick = (blogId) => {
    setBlogToDelete(blogId)
    setShowDeleteConfirmation(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${API_BASE}/${blogToDelete}`)
      setMessage("Blog deleted successfully!")
      fetchBlogs()
    } catch (err) {
      setError("Failed to delete blog.")
      console.error(err)
    } finally {
      setShowDeleteConfirmation(false)
      setBlogToDelete(null)
    }
  }

  return (
    <div className="container">
      <h1 className="text-center">Admin Panel - Blogs</h1>
      {message && (
        <Alert variant="success" className="text-center" onClose={() => setMessage("")} dismissible>
          {message}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" className="text-center" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      <div className="mb-3">
        <Button
          variant="primary"
          onClick={() => {
            resetForm()
            setShowModal(true)
          }}
        >
          Add Blog
        </Button>
      </div>

      {/* Add/Edit Blog Modal */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false)
          resetForm()
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{editBlogId ? "Edit Blog" : "Add Blog"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="e.g., How to maintain your sewing machine"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Author</Form.Label>
              <Form.Control
                type="text"
                name="author"
                value={formData.author}
                onChange={handleChange}
                placeholder="e.g., Madhu Team"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Control
                type="text"
                name="category"
                value={formData.category}
                onChange={handleChange}
                placeholder="e.g., Tips, News, Guides"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Cover Image</Form.Label>
              <div className="d-flex gap-2 mb-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => document.getElementById("blogImageInput").click()}
                >
                  {previewUrl ? "Change Image" : "Select Image"}
                </Button>
                {previewUrl && (
                  <Button variant="outline-danger" size="sm" onClick={handleRemoveImage}>
                    Remove Image
                  </Button>
                )}
              </div>
              <input
                id="blogImageInput"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
              {previewUrl && (
                <div className="mt-2">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="img-thumbnail"
                    style={{ width: "160px", height: "100px", objectFit: "cover" }}
                  />
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Content</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                name="content"
                value={formData.content}
                onChange={handleChange}
                required
                placeholder="Write the blog content here..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tags (comma-separated)</Form.Label>
              <Form.Control
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="e.g., maintenance, embroidery, tips"
              />
            </Form.Group>

            <Button variant="primary" type="submit" className="mt-2">
              {editBlogId ? "Update Blog" : "Add Blog"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirmation} onHide={() => setShowDeleteConfirmation(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this blog post?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirmation(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Blogs Table */}
      <h3 className="mt-5">Blog Posts</h3>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Image</th>
            <th>Title</th>
            <th>Author</th>
            <th>Category</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {blogs.map((blog) => (
            <tr key={blog._id}>
              <td>
                {blog.images && blog.images.length > 0 ? (
                  <img
                    src={`${UPLOADS_BASE}/${blog.images[0].url}`}
                    alt={blog.title}
                    className="img-thumbnail"
                    style={{ width: "60px", height: "60px", objectFit: "cover" }}
                  />
                ) : (
                  <span className="text-muted">No image</span>
                )}
              </td>
              <td>{blog.title}</td>
              <td>{blog.author || "-"}</td>
              <td>{blog.category || "-"}</td>
              <td>
                <Button variant="warning" size="sm" onClick={() => handleEdit(blog)} className="me-2">
                  Edit
                </Button>
                <Button variant="danger" size="sm" onClick={() => handleDeleteClick(blog._id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
          {blogs.length === 0 && (
            <tr>
              <td colSpan="5" className="text-center text-muted">
                No blog posts yet.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  )
}

export default AdminBlogs
